import {
  BuildingEnergyModelFragment,
  CreateScheduleInput,
  ScheduleFragment,
  ScheduleFragmentDoc,
  UpdateScheduleInput,
  useCreateScheduleMutation,
  useDeleteScheduleMutation,
  useUpdateScheduleMutation,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type ScheduleMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isDeleteLoading: boolean

  createSchedule: (
    buildingEnergyModel: BuildingEnergyModelFragment,
    input: CreateScheduleInput,
  ) => Promise<ScheduleFragment>

  updateSchedule: (
    id: string,
    input: UpdateScheduleInput,
  ) => Promise<ScheduleFragment>

  deleteSchedule: (
    buildingEnergyModel: BuildingEnergyModelFragment,
    id: string,
  ) => Promise<boolean>
}

export function useSchedules(): ScheduleMutations {
  const [createSchedule, { loading: isCreateLoading }] =
    useCreateScheduleMutation()
  const [updateSchedule, { loading: isUpdateLoading }] =
    useUpdateScheduleMutation()
  const [deleteSchedule, { loading: isDeleteLoading }] =
    useDeleteScheduleMutation()
  const { addItemToList, removeItemFromList } = useCacheHelpers()

  return {
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading,

    async createSchedule(
      buildingEnergyModel: BuildingEnergyModelFragment,
      input: CreateScheduleInput,
    ) {
      const result = await createSchedule({
        variables: {
          buildingEnergyModelId: buildingEnergyModel.id,
          input,
        },

        update: (_, mutationResult) => {
          const schedule = mutationResult.data?.createSchedule

          if (!schedule) return

          addItemToList(
            ScheduleFragmentDoc,
            schedule,
            buildingEnergyModel,
            'schedules',
          )
        },
      })

      if (!result.data) {
        throw new Error('Schedule creation failed')
      }

      return result.data.createSchedule
    },

    async updateSchedule(id: string, input: UpdateScheduleInput) {
      const result = await updateSchedule({ variables: { id, input } })

      if (!result.data) {
        throw new Error('Schedule update failed')
      }
      return result.data.updateSchedule
    },

    async deleteSchedule(
      buildingEnergyModel: BuildingEnergyModelFragment,
      id: string,
    ) {
      const result = await deleteSchedule({
        variables: { id },

        update: () => {
          removeItemFromList(
            ScheduleFragmentDoc,
            id,
            buildingEnergyModel,
            'schedules',
          )
        },

        optimisticResponse: {
          deleteSchedule: true,
        },
      })

      if (!result.data) {
        throw new Error('Schedule deletion failed')
      }

      return result.data.deleteSchedule
    },
  }
}
